import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AsyncPaginate from "react-select-async-paginate";
import api from "@/services/api";
import { helpers } from "@/services";
import { get } from "lodash";
import Modal from "@/components/Modal";
import config from "@/config";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  Filter,
  Pencil,
} from "lucide-react";
import QuickEditFieldsModal from "./components/QuickEditFieldsModal";
import "./style.scss";

const WEEK_DAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const getStateId = (s) => {
  const val = s.state_id ?? s.restored_status_id ?? s.id ?? s.status_id;
  return val === undefined || val === null ? null : val;
};

const getCurrencyTitle = (code) => {
  const found = config.CURRENCY_TYPES.find((c) => c.code === (code || "uzs"));
  return found ? found.title : "сум";
};

const loadBranches = async (search, _prev, { page }) => {
  const { data } = await api.request.get("/branches", {
    params: { page, name: search || undefined },
  });
  return {
    options: get(data, "data", []),
    hasMore: get(data, "meta.current_page", 1) < get(data, "meta.last_page", 1),
    additional: { page: get(data, "meta.current_page", 1) + 1 },
  };
};

const MODE_CONFIG = {
  install: {
    dateParamKey: "installation_date",
    groupUrl: "/employee/organizations/group/by-installation-day",
    countUrl: "/employee/organizations/count/by-installation-day",
    listUrl: "/employee/organizations/list/by-installation-day",
    title: "Дата установки",
    showAllChip: true,
  },
  restored: {
    dateParamKey: "restored_at",
    groupUrl: "/employee/organizations/group/by-restored-day",
    countUrl: "/employee/organizations/count/by-restored-day",
    listUrl: "/employee/organizations/list/by-restored-day",
    title: "Дата восстановления",
    showAllChip: true,
  },
  pricing: {
    countUrl: "/employee/organizations/count/by-installation-day",
    title: "Дни установки",
  },
};

const PRICING_DATE_FMT = "DD.MM.YYYY";

const InstallDateCalendar = ({
  type = "field",
  mode = "install",
  label,
  placeholder = "Выберите дату",
  required = false,
  size = "small",
  className = "",
  wrapperClassName = "",
  showBranchFilter = false,
  showStateFilter = true,
  extraParams = {},
  initialDate,
  onDateSelect,
  disabled = false,
}) => {
  const { dateParamKey, groupUrl, countUrl, listUrl, title, showAllChip } =
    MODE_CONFIG[mode] || MODE_CONFIG.install;
  const isPricing = mode === "pricing";
  const isRestored = mode === "restored";
  const parsedInitial = initialDate ? moment(initialDate) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(parsedInitial || moment());
  const [selectedDate, setSelectedDate] = useState(parsedInitial);
  const [branch, setBranch] = useState(null);
  const [confirmedDate, setConfirmedDate] = useState(parsedInitial);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [quickEditTarget, setQuickEditTarget] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    price: "",
    count: "",
    currency_code: "uzs",
  });
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const branchId = branch ? branch.id : undefined;
  const selectedKey = selectedDate ? selectedDate.format("YYYY-MM-DD") : null;

  const handleOpen = () => {
    setSelectedDate(confirmedDate);
    setCurrentMonth(confirmedDate || moment());
    setIsOpen(true);
  };

  const handleDayClick = (d) => {
    setSelectedDate(d);
    setActiveOrgId(null);
    if (isPricing) setPricingModalOpen(true);
  };

  const handleConfirm = () => {
    setConfirmedDate(selectedDate);
    if (onDateSelect) onDateSelect(selectedDate);
    setIsOpen(false);
    setSelectedStateId(null);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setConfirmedDate(null);
    if (onDateSelect) onDateSelect(null);
  };

  const apiParams = {
    ...(branchId ? { branch_id: branchId } : {}),
    ...extraParams,
  };

  // states + counts per state for chips
  const { data: groupData } = useQuery({
    queryKey: [
      mode,
      "day-group",
      selectedKey,
      currentMonth.format("YYYY-MM"),
      branchId,
      extraParams,
    ],
    queryFn: () =>
      api.request({
        url: groupUrl,
        params: selectedKey
          ? { [dateParamKey]: selectedKey, ...apiParams }
          : {
              start_at: currentMonth
                .clone()
                .startOf("month")
                .unix(),
              end_at: currentMonth
                .clone()
                .endOf("month")
                .unix(),
              ...apiParams,
            },
      }),
    enabled: isOpen && showStateFilter && !isPricing,
    staleTime: 30000,
  });

  const groupRaw = useMemo(() => {
    const raw = get(groupData, "data.data") || get(groupData, "data") || [];
    return Array.isArray(raw) ? raw : [];
  }, [groupData]);

  // filter out "Все" (state_id: 0) — show separately
  const states = useMemo(
    () => groupRaw.filter((s) => String(getStateId(s)) !== "0"),
    [groupRaw],
  );

  const totalState = useMemo(
    () => groupRaw.find((s) => String(getStateId(s)) === "0") || null,
    [groupRaw],
  );

  // calendar day counts (per-day numbers on cells)
  const { data: countsData, isFetching: countsLoading } = useQuery({
    queryKey: [
      mode,
      "day-counts",
      currentMonth
        .clone()
        .startOf("month")
        .unix(),
      branchId,
      extraParams,
      selectedStateId,
    ],
    queryFn: () =>
      api.request({
        url: countUrl,
        params: {
          start_at: currentMonth
            .clone()
            .startOf("month")
            .unix(),
          end_at: currentMonth
            .clone()
            .endOf("month")
            .unix(),
          ...apiParams,
          ...(selectedStateId !== null ? { state_id: selectedStateId } : {}),
        },
      }),
    enabled: isOpen,
    staleTime: 30000,
  });

  const counts = useMemo(() => {
    const raw = get(countsData, "data.data");
    if (!Array.isArray(raw)) return {};
    return raw.reduce((acc, item) => {
      const quantity = Number(item.quantity || 0);
      const successQuantity = Number(item.success_quantity || 0);
      const processQuantity = Number(item.process_quantity || 0);
      const cancelQuantity = Number(item.cancel_quantity || 0);
      const undefinedQuantity = Number(item.undefined_quantity || 0);
      const percent =
        quantity > 0 ? Math.round((successQuantity / quantity) * 100) : 0;
      acc[item.day] = {
        quantity,
        successQuantity,
        processQuantity,
        cancelQuantity,
        undefinedQuantity,
        percent,
      };
      return acc;
    }, {});
  }, [countsData]);

  const getPercentTier = (percent) => {
    if (percent >= 50) return "high";
    if (percent > 0) return "mid";
    return "low";
  };

  const statusListUrl =
    mode === "restored"
      ? "/organization-restored-statuses"
      : "/organization-states";

  const { data: statusListData } = useQuery({
    queryKey: ["ical-status-list", statusListUrl],
    queryFn: () => api.request.get(statusListUrl),
    enabled: isOpen && !isPricing,
    staleTime: 5 * 60 * 1000,
  });

  const statusNameById = useMemo(() => {
    const raw =
      get(statusListData, "data.data") || get(statusListData, "data") || [];
    const list = Array.isArray(raw) ? raw : [];
    return list.reduce((acc, item) => {
      acc[item.id] = item.name;
      return acc;
    }, {});
  }, [statusListData]);

  const { data: listData, isFetching: listLoading } = useQuery({
    queryKey: [
      mode,
      "day-list",
      selectedKey,
      branchId,
      extraParams,
      selectedStateId,
    ],
    queryFn: () =>
      api.request({
        url: listUrl,
        params: {
          [dateParamKey]: selectedKey,
          include: "region,district,sales,installer",
          ...apiParams,
          ...(selectedStateId !== null ? { state_id: selectedStateId } : {}),
        },
      }),
    enabled: !!selectedKey && !isPricing,
    staleTime: 30000,
  });

  const orgList = Array.isArray(get(listData, "data"))
    ? get(listData, "data")
    : [];

  // ── Pricing calendar (installation-days) ─────────────────────────────────
  const { data: pricingData, isFetching: pricingLoading } = useQuery({
    queryKey: [
      "pricing",
      "installation-days",
      currentMonth.month() + 1,
      currentMonth.year(),
    ],
    queryFn: () =>
      api.request.get("/installation-days", {
        params: {
          month: currentMonth.month() + 1,
          year: currentMonth.year(),
        },
      }),
    enabled: isOpen && isPricing,
    staleTime: 30000,
    refetchOnMount: false,
  });

  const pricingByDate = useMemo(() => {
    const raw = get(pricingData, "data.data") || get(pricingData, "data") || [];
    const list = Array.isArray(raw) ? raw : [];
    return list.reduce((acc, item) => {
      if (item && item.date) acc[item.date] = item;
      return acc;
    }, {});
  }, [pricingData]);

  useEffect(() => {
    if (!isPricing || !pricingModalOpen || !selectedDate) return;
    const entry = pricingByDate[selectedDate.format(PRICING_DATE_FMT)];
    const dayCount = get(counts, [
      selectedDate.format("YYYY-MM-DD"),
      "quantity",
    ]);
    setPricingForm({
      price: entry ? String(entry.price ?? "") : "",
      count: entry
        ? String(entry.count ?? "")
        : dayCount
        ? String(dayCount)
        : "",
      currency_code: (entry && entry.currency_code) || "uzs",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingModalOpen, selectedDate, isPricing]);

  useEffect(() => {
    const parsed = initialDate ? moment(initialDate) : null;
    setConfirmedDate(parsed);
    setSelectedDate(parsed);
  }, [initialDate]);

  const handlePricingSave = async () => {
    if (!selectedDate) return;
    setPricingSaving(true);
    try {
      await api.request.post("/installation-days", {
        date: selectedDate.format(PRICING_DATE_FMT),
        type: true,
        price: Number(pricingForm.price) || 0,
        count: Number(pricingForm.count) || 0,
        currency_code: pricingForm.currency_code || "uzs",
      });
      await queryClient.invalidateQueries({
        queryKey: ["pricing", "installation-days"],
        refetchType: "active",
      });
      setPricingModalOpen(false);
    } finally {
      setPricingSaving(false);
    }
  };

  const calDays = useMemo(() => {
    const start = currentMonth
      .clone()
      .startOf("month")
      .startOf("isoWeek");
    const end = currentMonth
      .clone()
      .endOf("month")
      .endOf("isoWeek");
    const days = [];
    const cur = start.clone();
    while (cur.isSameOrBefore(end, "day")) {
      days.push(cur.clone());
      cur.add(1, "day");
    }
    return days;
  }, [currentMonth]);

  return (
    <>
      <div
        className={`form-field form-field--${size} ${wrapperClassName}`.trim()}
      >
        {(label || required) && (
          <label className="form-field__label">
            {label}
            {required && <span title="Обязательный поля"> *</span>}
          </label>
        )}
        <div className="input-group">
          <button
            type="button"
            className={`ical-trigger form-field__input ${className}${
              disabled ? " ical-trigger--disabled" : ""
            }`.trim()}
            onClick={disabled ? undefined : handleOpen}
            disabled={disabled}
          >
            <span
              className={
                "ical-trigger__text" +
                (confirmedDate || type === "btn"
                  ? ""
                  : " ical-trigger__text--placeholder")
              }
            >
              {confirmedDate ? confirmedDate.format("DD.MM.YYYY") : placeholder}
            </span>
            {confirmedDate && type === "field" ? (
              <span
                className="ical-trigger__clear"
                onClick={handleClear}
                title="Очистить"
              >
                <X size={13} />
              </span>
            ) : (
              <span className="ical-trigger__icon">
                <Calendar
                  size={15}
                  color={type === "btn" ? "#1f2937" : "#bfbfbf"}
                />
              </span>
            )}
          </button>
        </div>
        {disabled && (
          <div className="ical-disabled-hint">
            Для выбора даты установки сначала выберите область и район
          </div>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        toggle={() => {
          setIsOpen(false);
          setSelectedStateId(null);
        }}
        size={1100}
        backdrop={false}
      >
        <div className="ical-modal-body">
          <div className="ical-title">
            <span className="ical-title__icon">
              <Calendar size={16} />
            </span>
            <span className="ical-title__text">{title}</span>
          </div>

          <div
            className={`ical-layout${isPricing ? " ical-layout--pricing" : ""}`}
          >
            {/* ─── Calendar column ─── */}
            <div
              className={`ical-cal${isPricing ? " ical-cal--wide" : ""}${
                isRestored ? " ical-cal--restored" : ""
              }`}
            >
              {showBranchFilter && (
                <div className="ical-branch-select">
                  <AsyncPaginate
                    value={branch}
                    loadOptions={loadBranches}
                    onChange={setBranch}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    placeholder="Филиал"
                    isClearable
                    additional={{ page: 1 }}
                    classNamePrefix="ical-select"
                  />
                </div>
              )}

              <div className="ical-nav">
                <button
                  type="button"
                  className="ical-nav__btn"
                  onClick={() =>
                    setCurrentMonth((m) => m.clone().subtract(1, "month"))
                  }
                >
                  <ChevronLeft size={15} />
                </button>
                <span className="ical-nav__label">
                  {currentMonth.format("MMMM YYYY")}
                </span>
                <button
                  type="button"
                  className="ical-nav__btn"
                  onClick={() =>
                    setCurrentMonth((m) => m.clone().add(1, "month"))
                  }
                >
                  <ChevronRight size={15} />
                </button>
              </div>

              <div className="ical-weekdays">
                {WEEK_DAYS.map((w) => (
                  <div key={w} className="ical-weekday">
                    {w}
                  </div>
                ))}
              </div>

              <div className="ical-grid-wrap">
                {(countsLoading || pricingLoading) && (
                  <div className="ical-grid-loading">
                    <div className="ical-spinner" />
                  </div>
                )}
                <div
                  className={`ical-grid${
                    isRestored ? " ical-grid--restored" : ""
                  }`}
                >
                  {calDays.map((d) => {
                    if (isPricing) {
                      const key = d.format("YYYY-MM-DD");
                      const inMon = d.month() === currentMonth.month();
                      const isToday = d.isSame(moment(), "day");
                      const isPast = d.isBefore(moment(), "day");
                      const isSel = key === selectedKey;
                      const pEntry = inMon
                        ? pricingByDate[d.format(PRICING_DATE_FMT)]
                        : null;
                      const isOverride = !!(pEntry && pEntry.type);
                      const realCount = inMon
                        ? get(counts, [key, "quantity"], 0)
                        : 0;
                      const overrideCount = pEntry
                        ? Number(pEntry.count || 0)
                        : null;
                      const hasPrice =
                        pEntry &&
                        pEntry.price !== undefined &&
                        pEntry.price !== null;
                      const pPrice = hasPrice ? Number(pEntry.price) : null;

                      let dayClass = "ical-day ical-day--capacity";
                      if (!inMon) {
                        dayClass += " ical-day--out";
                      } else {
                        if (isPast) dayClass += " ical-day--past";
                        if (isToday) dayClass += " ical-day--today";
                        if (isSel) dayClass += " ical-day--sel";
                      }

                      return (
                        <div
                          key={key}
                          className={dayClass}
                          onClick={() => {
                            if (inMon) handleDayClick(d);
                          }}
                        >
                          {inMon && (
                            <>
                              <span className="ical-day__weekday-label">
                                {WEEK_DAYS[d.isoWeekday() - 1].toUpperCase()}
                              </span>
                              {isOverride && (
                                <span
                                  className="ical-day__count--override"
                                  title="Установлено вручную"
                                >
                                  {overrideCount}
                                </span>
                              )}
                              <span
                                className={`ical-day__count--capacity${
                                  realCount > 0
                                    ? " ical-day__count--capacity-busy"
                                    : " ical-day__count--capacity-free"
                                }`}
                                title="Реальные данные"
                              >
                                {realCount}
                              </span>
                              <span className="ical-day__num ical-day__num--bold">
                                {d.date()}
                              </span>
                              {isOverride && (
                                <span className="ical-day__price">
                                  {hasPrice
                                    ? `${pPrice.toLocaleString(
                                        "ru-RU",
                                      )} ${getCurrencyTitle(
                                        pEntry.currency_code,
                                      )}`
                                    : "Цена не указана"}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      );
                    }

                    const key = d.format("YYYY-MM-DD");
                    const inMon = d.month() === currentMonth.month();
                    const isToday = d.isSame(moment(), "day");
                    const isSel = key === selectedKey;
                    const dayData = inMon ? counts[key] : null;
                    const count = dayData ? dayData.quantity : 0;
                    const percent = dayData ? dayData.percent : 0;

                    const hasTier = !isRestored && count > 0;
                    const hasStats = isRestored && count > 0;

                    let dayClass = "ical-day";
                    if (isRestored) dayClass += " ical-day--restored";
                    if (!inMon) {
                      dayClass += " ical-day--out";
                    } else {
                      if (hasTier) {
                        dayClass += ` ical-day--tier-${getPercentTier(
                          percent,
                        )}`;
                      } else if (isToday) {
                        dayClass += " ical-day--today";
                      }
                      if (isSel) {
                        dayClass += hasTier
                          ? " ical-day--sel-tier"
                          : " ical-day--sel";
                      }
                    }

                    let numClass = "ical-day__num";
                    if (isSel || isToday) numClass += " ical-day__num--bold";
                    if (!hasTier) {
                      if (isSel) numClass += " ical-day__num--sel";
                      else if (isToday) numClass += " ical-day__num--today";
                    }

                    return (
                      <div
                        key={key}
                        className={dayClass}
                        onClick={() => {
                          if (inMon) handleDayClick(d);
                        }}
                      >
                        {!isRestored && count > 0 && (
                          <>
                            <span className="ical-day__count">{count}</span>
                            <span
                              className={`ical-day__percent ical-day__percent--${getPercentTier(
                                percent,
                              )}`}
                            >
                              {percent}%
                            </span>
                          </>
                        )}
                        {isRestored ? (
                          <div className="ical-day__restored-row">
                            <span className={numClass}>{d.date()}</span>
                            {hasStats && (
                              <div className="ical-day__stats">
                                <span
                                  className="ical-day__stat ical-day__stat--total"
                                  title="Всего"
                                >
                                  {dayData.quantity}
                                </span>
                                <span
                                  className="ical-day__stat ical-day__stat--success"
                                  title="Успешно"
                                >
                                  {dayData.successQuantity}
                                </span>
                                <span
                                  className="ical-day__stat ical-day__stat--process"
                                  title="В процессе"
                                >
                                  {dayData.processQuantity}
                                </span>
                                <span
                                  className="ical-day__stat ical-day__stat--undefined"
                                  title="Не указано"
                                >
                                  {dayData.undefinedQuantity}
                                </span>
                                <span
                                  className="ical-day__stat ical-day__stat--cancel"
                                  title="Отменено"
                                >
                                  {dayData.cancelQuantity}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={numClass}>{d.date()}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {isPricing && (
                <div className="ical-legend">
                  <span className="ical-legend__item">
                    <span className="ical-legend__dot ical-legend__dot--override" />
                    Введено вручную
                  </span>
                  <span className="ical-legend__item">
                    <span className="ical-legend__dot ical-legend__dot--busy" />
                    Реальные данные
                  </span>
                </div>
              )}

              {isRestored && (
                <div className="ical-legend">
                  <span className="ical-legend__item">
                    <span className="ical-legend__dot ical-legend__dot--total" />
                    Всего
                  </span>
                  <span className="ical-legend__item">
                    <span className="ical-legend__dot ical-legend__dot--success" />
                    Успешно
                  </span>
                  <span className="ical-legend__item">
                    <span className="ical-legend__dot ical-legend__dot--process" />
                    В процессе
                  </span>
                  <span className="ical-legend__item">
                    <span className="ical-legend__dot ical-legend__dot--cancel" />
                    Отменено
                  </span>
                  <span className="ical-legend__item">
                    <span className="ical-legend__dot ical-legend__dot--undefined" />
                    Не указано
                  </span>
                </div>
              )}

              {type === "field" && selectedDate && !isPricing && (
                <button
                  type="button"
                  className="ical-set-btn"
                  onClick={handleConfirm}
                >
                  Выбрать {selectedDate.format("DD.MM.YYYY")}
                </button>
              )}
            </div>

            {/* ─── Right panel ─── */}
            {!isPricing && (
              <div className="ical-panel">
                {/* State filter */}
                {showStateFilter && (states.length > 0 || totalState) && (
                  <div className="ical-state-filter">
                    <div className="ical-state-filter__header">
                      <Filter size={13} />
                      <span>Статус</span>
                    </div>
                    <div className="ical-state-filter__chips">
                      {showAllChip && (
                        <button
                          type="button"
                          className={`ical-state-chip${
                            selectedStateId === null
                              ? " ical-state-chip--active"
                              : ""
                          }`}
                          onClick={() => setSelectedStateId(null)}
                        >
                          {totalState ? totalState.name : "Все"}
                          {totalState && (
                            <span className="ical-state-chip__count">
                              {totalState.count}
                            </span>
                          )}
                        </button>
                      )}
                      {states.map((s) => {
                        const sid = getStateId(s);
                        return (
                          <button
                            key={sid ?? s.name}
                            type="button"
                            className={`ical-state-chip${
                              String(selectedStateId) === String(sid)
                                ? " ical-state-chip--active"
                                : ""
                            }`}
                            onClick={() => setSelectedStateId(sid)}
                          >
                            {s.name}
                            <span className="ical-state-chip__count">
                              {s.count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedDate ? (
                  <div className="ical-panel__body">
                    <div className="ical-panel__date-label">
                      {selectedDate.format("DD MMMM YYYY")}
                    </div>
                    <div
                      className={`ical-tasks${
                        listLoading ? " ical-tasks--loading" : ""
                      }`}
                    >
                      {listLoading ? (
                        <div className="ical-empty">
                          <div className="ical-spinner" />
                        </div>
                      ) : orgList.length === 0 ? (
                        <div className="ical-empty">
                          Нет данных за этот день
                        </div>
                      ) : (
                        <div className="ical-task-list">
                          {orgList.map((org, idx) => {
                            const isExpanded = activeOrgId === org.id;
                            return (
                              <div
                                key={org.id || idx}
                                className="ical-task-wrap"
                              >
                                <div className="ical-task-wrap__header">
                                  <span className="ical-task-wrap__author">
                                    {get(org, "employee_name") || "—"}
                                  </span>
                                  <span className="ical-task__date">
                                    {mode === "restored"
                                      ? statusNameById[
                                          get(org, "restored_status_id")
                                        ] ||
                                        get(org, "restored_status.name") ||
                                        get(org, "restored_status_id") ||
                                        ""
                                      : statusNameById[get(org, "state_id")] ||
                                        get(org, "state.name") ||
                                        get(org, "state_id") ||
                                        ""}
                                    <button
                                      type="button"
                                      className="ical-task__date-edit"
                                      title="Изменить"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuickEditTarget({
                                          orgId: org.id,
                                          mode,
                                        });
                                      }}
                                    >
                                      <Pencil size={12} />
                                    </button>
                                  </span>
                                </div>
                                <div
                                  className={`ical-task${
                                    isExpanded ? " ical-task--expanded" : ""
                                  }`}
                                  onClick={() =>
                                    setActiveOrgId(isExpanded ? null : org.id)
                                  }
                                >
                                  <span className="ical-task__badge">
                                    {idx + 1}
                                  </span>
                                  <p className="ical-task__text">
                                    <span>{get(org, "name") || "—"}</span>
                                    <span>
                                      {get(org, "region.name") || "—"},{" "}
                                      {get(org, "district.name") || "—"}
                                    </span>
                                  </p>
                                </div>
                                {isExpanded && (
                                  <div
                                    className="ical-task-details"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="ical-detail-row">
                                      <span className="ical-detail-row__label">
                                        ID:
                                      </span>
                                      <span className="ical-detail-row__value">
                                        {get(org, "payment_id") || "—"}
                                      </span>
                                    </div>
                                    <div className="ical-detail-row">
                                      <span className="ical-detail-row__label">
                                        Статус:
                                      </span>
                                      <span className="ical-detail-row__value">
                                        {mode === "restored"
                                          ? statusNameById[
                                              get(org, "restored_status_id")
                                            ] ||
                                            get(org, "restored_status.name") ||
                                            get(org, "restored_status_id") ||
                                            "—"
                                          : statusNameById[
                                              get(org, "state_id")
                                            ] ||
                                            get(org, "state.name") ||
                                            get(org, "state_id") ||
                                            "—"}
                                      </span>
                                    </div>
                                    <div className="ical-detail-row">
                                      <span className="ical-detail-row__label">
                                        Продавец:
                                      </span>
                                      <span className="ical-detail-row__value">
                                        {get(org, "sales.name") || "—"}
                                      </span>
                                    </div>
                                    <div className="ical-detail-row">
                                      <span className="ical-detail-row__label">
                                        Установщик:
                                      </span>
                                      <span className="ical-detail-row__value">
                                        {get(org, "installer.name") || "—"}
                                      </span>
                                    </div>
                                    <div className="ical-detail-row">
                                      <span className="ical-detail-row__label">
                                        Тел. владельца:
                                      </span>
                                      <span className="ical-detail-row__value">
                                        {helpers.formatPhone(
                                          get(org, "owner_phone"),
                                        )}
                                      </span>
                                    </div>
                                    {Array.isArray(get(org, "phones")) &&
                                      get(org, "phones").length > 0 && (
                                        <div className="ical-detail-row ical-detail-row--phones">
                                          <span className="ical-detail-row__label">
                                            Доп. телефоны:
                                          </span>
                                          <div className="ical-detail-row__value ical-detail-phones">
                                            {get(org, "phones").map(
                                              (p, pIdx) => (
                                                <div
                                                  key={pIdx}
                                                  className="ical-phone-item"
                                                >
                                                  <strong>
                                                    {p.name ||
                                                      p.position ||
                                                      `Тел ${pIdx + 1}`}
                                                    :
                                                  </strong>{" "}
                                                  {helpers.formatPhone(p.phone)}
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="ical-no-date">
                    <span className="ical-no-date__title">Выберите дату</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {isPricing && (
        <Modal
          isOpen={pricingModalOpen}
          toggle={() => setPricingModalOpen(false)}
          size={450}
          zIndex={1070}
        >
          <div className="ical-edit-org">
            <div className="ical-title">
              <span className="ical-title__icon">
                <Calendar size={16} />
              </span>
              <span className="ical-title__text">
                {selectedDate ? selectedDate.format("DD MMMM YYYY") : ""}
              </span>
            </div>

            <div className="ical-capacity-form">
              <div className="ical-capacity-form__field">
                <label>Количество мест</label>
                <input
                  type="number"
                  min="0"
                  value={pricingForm.count}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPricingForm((f) => ({ ...f, count: value }));
                  }}
                  placeholder="0"
                />
              </div>
              <div className="ical-capacity-form__field">
                <label>Цена</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={
                    pricingForm.price
                      ? Number(pricingForm.price).toLocaleString("ru-RU")
                      : ""
                  }
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    setPricingForm((f) => ({ ...f, price: digits }));
                  }}
                  placeholder="0"
                />
              </div>
              <div className="ical-edit-org__actions">
                <button
                  type="button"
                  className="ical-edit-org__cancel"
                  onClick={() => setPricingModalOpen(false)}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="ical-set-btn"
                  disabled={pricingSaving}
                  onClick={handlePricingSave}
                >
                  {pricingSaving ? "Сохранение..." : "Сохранить"}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <QuickEditFieldsModal
        isOpen={!!quickEditTarget}
        orgId={quickEditTarget && quickEditTarget.orgId}
        mode={quickEditTarget && quickEditTarget.mode}
        toggle={() => setQuickEditTarget(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: [mode, "day-list"] });
          queryClient.invalidateQueries({ queryKey: [mode, "day-group"] });
          queryClient.invalidateQueries({ queryKey: [mode, "day-counts"] });
          setQuickEditTarget(null);
        }}
      />
    </>
  );
};

export default InstallDateCalendar;
